import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import {
  ArrowLeft,
  Store as StoreIcon,
  Mail,
  Phone,
  DollarSign,
  ShoppingCart,
  Package,
  Users,
  Edit,
  CheckCircle,
  AlertCircle,
  Globe,
  TrendingUp,
  Activity,
  ChevronRight,
  Shield,
  Clock,
  CreditCard,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { StoreStatus, type Store, type User } from '@dukene/shared';



interface StoreStats {
  totalRevenue: number;
  totalOrders: number;
  totalProducts: number;
  totalCustomers: number;
  revenueTrend: { date: string; amount: number }[];
  topProducts: {
    id: string;
    name: string;
    image?: string;
    revenue: number;
    quantity: number;
  }[];
  recentActivity: {
    type: 'ORDER' | 'PRODUCT' | 'SYSTEM';
    title: string;
    description: string;
    time: string;
  }[];
}

interface ExtendedUser extends User {
  lastLoginAt?: string;
}

interface StoreTransaction {
  id: string;
  type: string;
  amount: number;
  currency: string;
  status: string;
  referenceId?: string;
  referenceType?: string;
  description?: string;
  createdAt: string;
}

interface StorePayout {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  destination?: string;
  transactionId?: string;
  processedAt?: string;
  createdAt: string;
}

interface ExtendedStore extends Store {
  users: ExtendedUser[];
  storeMilestones: {
    id: string;
    completed: boolean;
    completedAt: string | null;
    progress: number;
    milestone: {
      title: string;
      description: string;
      icon: string;
    };
  }[];
}

export default function StoreDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'staff' | 'transactions' | 'settings'>('overview');

  // Fetch store details
  const { data: store, isLoading, error } = useQuery<ExtendedStore>({
    queryKey: ['store', id],
    queryFn: async () => {
      return apiClient.get<ExtendedStore>(`/stores/${id}`);
    },
  });

  // Fetch store stats
  const { data: stats } = useQuery<StoreStats>({
    queryKey: ['store-stats', id],
    queryFn: async () => {
      return apiClient.get<StoreStats>(`/stores/${id}/stats`);
    },
    enabled: !!id,
  });

  // Fetch store transactions
  const { data: transactions, isLoading: isLoadingTransactions } = useQuery<StoreTransaction[]>({
    queryKey: ['store-transactions', id],
    queryFn: async () => {
      return apiClient.get<StoreTransaction[]>(`/stores/${id}/transactions`);
    },
    enabled: !!id && activeTab === 'transactions',
  });

  // Fetch store payouts
  const { data: payouts, isLoading: isLoadingPayouts } = useQuery<StorePayout[]>({
    queryKey: ['store-payouts', id],
    queryFn: async () => {
      return apiClient.get<StorePayout[]>(`/stores/${id}/payouts`);
    },
    enabled: !!id && activeTab === 'transactions',
  });

  // Update store status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status: StoreStatus) => {
      return apiClient.patch(`/stores/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', id] });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });

  // Update payout status mutation
  const updatePayoutStatusMutation = useMutation({
    mutationFn: async ({ payoutId, status }: { payoutId: string; status: string }) => {
      return apiClient.patch(`/stores/${id}/payouts/${payoutId}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-payouts', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading store details...</p>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-black text-black mb-2">Failed to Load Store</h2>
            <p className="text-gray-500">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/stores')}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-400 hover:text-black hover:border-black transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black text-black">{store.name}</h1>
              <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${store.status === StoreStatus.ACTIVE ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                {store.status}
              </span>
            </div>
            <p className="text-sm text-gray-500">{store.subdomain}.dukene.com</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`https://${store.subdomain}.dukene.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="h-10 px-4 flex items-center gap-2 text-sm font-bold rounded-xl bg-white border border-gray-200 text-black hover:border-black transition-all"
          >
            <Globe className="w-4 h-4" />
            Visit
          </a>
          <button
            onClick={() => setActiveTab('settings')}
            className="h-10 px-4 flex items-center gap-2 text-sm font-bold rounded-xl bg-black text-white hover:bg-gray-800 transition-all"
          >
            <Edit className="w-4 h-4" />
            Manage Store
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-2xl w-fit">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'transactions', label: 'Transactions', icon: FileText },
          { id: 'staff', label: 'Team', icon: Users },
          { id: 'settings', label: 'Configuration', icon: Shield },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all ${activeTab === tab.id
              ? 'bg-white text-black shadow-sm'
              : 'text-gray-500 hover:text-black'
              }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Revenue', value: `KES ${stats?.totalRevenue?.toLocaleString()}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'Orders', value: stats?.totalOrders, icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-50' },
                { label: 'Products', value: stats?.totalProducts, icon: Package, color: 'text-violet-500', bg: 'bg-violet-50' },
                { label: 'Customers', value: stats?.totalCustomers, icon: Users, color: 'text-amber-500', bg: 'bg-amber-50' },
              ].map((stat, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-xl hover:shadow-gray-100 transition-all group">
                  <div className={`w-12 h-12 ${stat.bg} ${stat.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                  <p className="text-2xl font-black text-black mt-1">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Chart */}
              <div className="lg:col-span-2 bg-white border border-gray-200 rounded-3xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-lg font-black text-black">Revenue Trend</h3>
                    <p className="text-sm text-gray-500">Last 30 days performance</p>
                  </div>
                  <div className="px-3 py-1 bg-gray-100 rounded-lg text-[10px] font-black uppercase tracking-wider text-gray-500">
                    Growth: +12.5%
                  </div>
                </div>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats?.revenueTrend}>
                      <defs>
                        <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#000" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#000" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fontWeight: 'bold' }}
                        tickFormatter={(str) => format(new Date(str), 'MMM d')}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fontWeight: 'bold' }}
                        tickFormatter={(val) => `KES ${val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val}`}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '16px',
                          border: 'none',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#000"
                        strokeWidth={4}
                        fillOpacity={1}
                        fill="url(#colorRev)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Activity Feed */}
              <div className="bg-white border border-gray-200 rounded-3xl p-8">
                <h3 className="text-lg font-black text-black mb-6 flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </h3>
                <div className="space-y-6">
                  {stats?.recentActivity?.map((activity, i) => (
                    <div key={i} className="flex gap-4 group cursor-default">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border-2 border-white shadow-sm transition-transform group-hover:scale-110 ${activity.type === 'ORDER' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                          {activity.type === 'ORDER' ? <ShoppingCart className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                        </div>
                        {i !== stats.recentActivity.length - 1 && (
                          <div className="w-px h-full bg-gray-100 my-1" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="text-sm font-black text-black leading-none">{activity.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                        <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest mt-2">
                          {format(new Date(activity.time), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Products */}
              <div className="bg-white border border-gray-200 rounded-3xl p-8">
                <h3 className="text-lg font-black text-black mb-6">Top Performing Products</h3>
                <div className="space-y-4">
                  {stats?.topProducts?.map((product) => (
                    <div key={product.id} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-gray-50 transition-all group">
                      <div className="w-14 h-14 rounded-xl bg-gray-100 shrink-0 overflow-hidden border border-gray-100">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-black truncate">{product.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{product.quantity} units sold</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black text-black">KES {product.revenue.toLocaleString()}</p>
                        <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-0.5">High Demand</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Onboarding Progress */}
              <div className="bg-white border border-gray-200 rounded-3xl p-8">
                <h3 className="text-lg font-black text-black mb-6">Onboarding Checkpoint</h3>
                <div className="space-y-4">
                  {store.storeMilestones?.map((sm) => (
                    <div key={sm.id} className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${sm.completed ? 'bg-black text-white' : 'bg-gray-50 text-gray-400'}`}>
                        {sm.completed ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-black ${sm.completed ? 'text-black' : 'text-gray-500'}`}>{sm.milestone.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{sm.milestone.description}</p>
                      </div>
                      {sm.completedAt && (
                        <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest shrink-0">
                          {format(new Date(sm.completedAt), 'MMM d')}
                        </p>
                      )}
                    </div>
                  ))}
                  {(!store.storeMilestones || store.storeMilestones.length === 0) && (
                    <div className="py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                      <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-bold text-gray-400">No milestones tracked yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-3xl p-8">
              <h3 className="text-xl font-black text-black mb-6">Store Payouts</h3>
              {isLoadingPayouts ? (
                <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-widest">
                        <th className="pb-4">Amount</th>
                        <th className="pb-4">Provider</th>
                        <th className="pb-4">Status</th>
                        <th className="pb-4">Date</th>
                        <th className="pb-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payouts?.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-gray-500">No payouts found</td>
                        </tr>
                      ) : (
                        payouts?.map((payout) => (
                          <tr key={payout.id} className="group">
                            <td className="py-4 font-black flex flex-col">
                              <span className="text-black">{payout.currency} {payout.amount.toLocaleString()}</span>
                            </td>
                            <td className="py-4 font-medium text-gray-600">
                              {payout.provider}
                              {payout.destination && <span className="block text-xs text-gray-400 font-normal">{payout.destination}</span>}
                            </td>
                            <td className="py-4">
                              <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                payout.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                                payout.status === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {payout.status}
                              </span>
                            </td>
                            <td className="py-4 text-sm text-gray-500">{format(new Date(payout.createdAt), 'PP')}</td>
                            <td className="py-4 text-right">
                              {payout.status === 'PENDING' && (
                                <button
                                  onClick={() => updatePayoutStatusMutation.mutate({ payoutId: payout.id, status: 'COMPLETED' })}
                                  className="px-3 py-1.5 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-colors"
                                  disabled={updatePayoutStatusMutation.isPending}
                                >
                                  Approve
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8">
              <h3 className="text-xl font-black text-black mb-6">Recent Transactions</h3>
              {isLoadingTransactions ? (
                <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-widest">
                        <th className="pb-4">Type</th>
                        <th className="pb-4">Amount</th>
                        <th className="pb-4">Status</th>
                        <th className="pb-4">Reference</th>
                        <th className="pb-4 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions?.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-gray-500">No transactions found</td>
                        </tr>
                      ) : (
                        transactions?.map((tx) => (
                          <tr key={tx.id} className="group">
                            <td className="py-4 font-black items-center gap-2">
                              <span className="text-black flex gap-2">
                                {tx.type === 'SALE' ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <CreditCard className="w-4 h-4 text-blue-500" />}
                                {tx.type}
                              </span>
                            </td>
                            <td className={`py-4 font-black ${tx.type === 'SALE' ? 'text-emerald-600' : 'text-gray-900'}`}>{tx.currency} {Number(tx.amount).toLocaleString()}</td>
                            <td className="py-4">
                              <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                tx.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {tx.status}
                              </span>
                            </td>
                            <td className="py-4 text-sm text-gray-500">
                              <span className="block font-bold pb-0.5">{tx.referenceType}</span>
                              {tx.referenceId && `(${tx.referenceId})`}
                            </td>
                            <td className="py-4 text-sm text-gray-500 text-right">{format(new Date(tx.createdAt), 'PP')}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'staff' && (
          <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="p-8 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-black">Store Team</h3>
                <p className="text-sm text-gray-500">Manage owner and staff access</p>
              </div>
              <button className="h-10 px-4 flex items-center gap-2 text-sm font-bold rounded-xl bg-black text-white hover:bg-gray-800 transition-all">
                <Users className="w-4 h-4" />
                Add Member
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400">Member</th>
                    <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400">Role</th>
                    <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400">Status</th>
                    <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400">Last Active</th>
                    <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {store.users?.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center font-black text-black">
                            {user.firstName[0]}{user.lastName[0]}
                          </div>
                          <div>
                            <p className="text-sm font-black text-black">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${user.role === 'OWNER' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                          <span className="text-xs font-bold text-gray-600 capitalize">{user.status?.toLowerCase()}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-xs font-medium text-gray-500">
                        {user.lastLoginAt ? format(new Date(user.lastLoginAt), 'MMM d, yyyy') : 'Never'}
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-black transition-all">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Store Info Card */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
              <h3 className="text-lg font-black text-black mb-6 flex items-center gap-2">
                <StoreIcon className="w-5 h-5" />
                Store Information
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Subdomain</p>
                    <p className="text-sm font-black text-black">{store.subdomain}.dukene.com</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Created</p>
                    <p className="text-sm font-black text-black">{format(new Date(store.createdAt), 'MMM d, yyyy')}</p>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Description</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{store.description || 'No description provided.'}</p>
                </div>

                <div className="pt-4 border-t border-gray-50 space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-black">{store.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-black">{store.phone || 'No phone set'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Config & Integration */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
              <h3 className="text-lg font-black text-black mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Integration Settings
              </h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-black">Meta Commerce</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Product Catalog Sync</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-wider">
                    Connected
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-black">WhatsApp Business</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Messaging API</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-wider">
                    Partial Sync
                  </span>
                </div>

                <div className="pt-4 space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">System Controls</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateStatusMutation.mutate(store.status === StoreStatus.ACTIVE ? StoreStatus.SUSPENDED : StoreStatus.ACTIVE)}
                      className={`flex-1 h-11 px-4 text-xs font-black rounded-xl border-2 transition-all ${store.status === StoreStatus.ACTIVE
                        ? 'border-gray-100 text-gray-400 hover:border-red-500 hover:text-red-500'
                        : 'border-black text-black hover:bg-black hover:text-white'
                        }`}
                    >
                      {store.status === StoreStatus.ACTIVE ? 'Suspend Store' : 'Activate Store'}
                    </button>
                    <button className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 hover:text-black transition-all">
                      <Activity className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
